import { Component, Input, Output, EventEmitter, OnInit, OnDestroy } from '@angular/core';
import { Subject, debounceTime, distinctUntilChanged, map, takeUntil } from 'rxjs';

export interface GridHeaderSelectOption {
  value: any;
  label: string;
}

export interface GridHeaderToggle {
  id: string;
  label: string;
  tooltip?: string;
  checked?: boolean;
}

@Component({
  selector: 'app-grid-header',
  templateUrl: './grid-header.component.html',
  styleUrls: ['./grid-header.component.scss']
})
export class GridHeaderComponent implements OnInit, OnDestroy {
  @Input() selectOptions: GridHeaderSelectOption[] = [];
  @Input() searchPlaceholder: string = 'Search';
  @Input() showSearch: boolean = false;
  @Input() selectedValue: any;
  @Input() toggles: GridHeaderToggle[] = [];
  @Input() legendText?: string;

  /**
   * The marker drawn beside legendText. Whatever a grid puts in the cell the legend explains has to
   * be passed here too, or the two drift apart - which is what happened while this was a CSS
   * ::before pinned to '*'.
   */
  @Input() legendIcon: string = 'star';

  /** Quiet period after the user stops typing before searchChange is emitted. */
  @Input() searchDebounceMs: number = 300;

  @Output() selectChange = new EventEmitter<any>();
  @Output() toggleChange = new EventEmitter<{id: string, checked: boolean}>();
  @Output() searchChange = new EventEmitter<string>();

  private readonly searchInput$ = new Subject<string>();
  private readonly destroy$ = new Subject<void>();

  ngOnInit(): void {
    // Every keystroke reaches searchInput$, but only a settled, actually-changed value is
    // emitted. Consumers refresh a server side grid on searchChange, so emitting per keyup
    // put a request chain on the wire for every character typed - and let an earlier, slower
    // response land after a later one.
    this.searchInput$
      .pipe(
        map(value => value?.trim() ?? ''),
        debounceTime(this.searchDebounceMs),
        // Drops keyups that did not change the text: arrow keys, modifiers, tab.
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe(value => this.searchChange.emit(value));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSelectChange(value: any) {
    this.selectChange.emit(value);
  }

  onToggleChange(toggleId: string, checked: boolean) {
    this.toggleChange.emit({ id: toggleId, checked });
  }

  onSearch(event: any) {
    this.searchInput$.next(event.target.value);
  }
}
